import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthentication } from '@connect-x-app/hybrid-state';
import { PushNotificationGroup } from '@connect-x-app/shared-domain-models';
import { EventType } from '@connect-x-app/shared-types';
import { COLORS } from '@connect-x-app/shared-ui';
import { getIconForUser, UserUtil } from '@connect-x-app/shared-utils';
import { useFirebaseConfig, useLogout } from '@connect-x-app/web-state';
import {
  Button,
  ConfirmationModal,
  ModalOpener,
  ProfileHeader,
  ProfileSection,
  SideModal,
  SideModalHeader,
} from '@connect-x-app/web-ui';

import { About } from '../about';
import { Settings } from '../settings';

import '@connect-x-app/web-ui-styles-shared/profile.scss';

type Props = {
  isVisible: boolean;
  onClose: () => void;
};

type Screen = 'PROFILE' | 'ABOUT' | 'SETTINGS';

export const Profile: FC<Props> = ({ isVisible, onClose }) => {
  const { t } = useTranslation();

  const { user } = useAuthentication();
  const { logEvent } = useFirebaseConfig();

  const logout = useLogout();

  const { email, phoneNumber, rank } = user ?? {};
  const [screenToDisplay, setScreenToDisplay] = useState<Screen>('PROFILE');
  // The settings screen is two levels deep: null is the group list, a group is its detail view.
  const [pushNotificationGroup, setPushNotificationGroup] = useState<PushNotificationGroup | null>(null);

  const showSettings = () => {
    setPushNotificationGroup(null);
    setScreenToDisplay('SETTINGS');
  };

  const onClickLogout = () => {
    ModalOpener.instance.open({
      render: () => (
        <ConfirmationModal
          cancelButtonTheme="dark"
          confirmButtonTheme="negative"
          confirmText={t('AUTH.LOGOUT')}
          content={t('AUTH.LOGOUT_ALERT_REDIRECT')}
          onConfirm={() => {
            logEvent(EventType.LOGOUT, { reason: 'Manual logout' });
            logout();
          }}
          title={t('AUTH.LOGOUT')}
        />
      ),
    });
  };

  const onClickBackToProfile = () => {
    if (pushNotificationGroup) {
      setPushNotificationGroup(null);
      return;
    }
    setScreenToDisplay('PROFILE');
  };

  const renderContent = () => {
    switch (screenToDisplay) {
      case 'ABOUT':
        return <About />;
      case 'SETTINGS':
        return <Settings group={pushNotificationGroup} onSelectGroup={setPushNotificationGroup} />;
      case 'PROFILE':
      default:
        return (
          <>
            <ProfileHeader
              firstName={user?.username}
              iconName={getIconForUser({ user })}
              jobTitle={UserUtil.getDivision(user?.jobTitle)}
            />
            <div className="profile-mid-bot-container">
              <section className="profile-section-mid-container">
                <ProfileSection icon="briefcase" title={t('PROFILE.JOB_TITLE')} value={rank} />
                <ProfileSection icon="phone" title={t('PROFILE.PHONE_NUMBER')} value={phoneNumber} />
                <ProfileSection icon="email" title={t('PROFILE.EMAIL')} value={email} />
              </section>
            </div>
          </>
        );
    }
  };

  const renderButtons = () => {
    if (screenToDisplay === 'PROFILE') {
      return (
        <>
          <Button className="profile-bottom-button" isOutline onClick={() => setScreenToDisplay('ABOUT')} theme="dark">
            {t('PROFILE.ABOUT')}
          </Button>
          <Button className="profile-bottom-button" onClick={onClickLogout}>
            {t('AUTH.LOGOUT')}
          </Button>
        </>
      );
    }

    return null;
  };

  const renderHeader = (): SideModalHeader => {
    switch (screenToDisplay) {
      case 'ABOUT':
        return {
          leftIcon: {
            iconName: 'arrowLeft',
            onClick: () => {
              setScreenToDisplay('PROFILE');
            },
          },
          title: {
            props: { uppercase: false },
            text: t('PROFILE.ABOUT'),
          },
        };
      case 'SETTINGS':
        return {
          leftIcon: {
            iconName: 'arrowLeft',
            onClick: onClickBackToProfile,
          },
          title: {
            props: { uppercase: false },
            text: t('SETTINGS.HEADER'),
          },
        };
      case 'PROFILE':
      default:
        return {
          backgroundColor: COLORS.victor.$03,
          leftIcon: {
            iconName: 'gear',
            onClick: showSettings,
          },
          rightIcon: {
            iconName: 'closeCross',
            onClick: onClose,
          },
          title: { props: { uppercase: false }, text: t('PROFILE.HEADER_TITLE') },
        };
    }
  };

  const handleBackgroundClick = () => {
    setScreenToDisplay('PROFILE');
    setPushNotificationGroup(null);
    onClose();
  };

  return (
    <SideModal
      bottomButtons={renderButtons()}
      content={renderContent()}
      fullwidth={screenToDisplay !== 'SETTINGS'}
      header={renderHeader()}
      isVisible={isVisible}
      onBackgroundClick={handleBackgroundClick}
      placement={'left'}
    />
  );
};
